/**
 * SuperAdminMetricsTab.jsx — Mission Control: API request metrics, error
 * rates, latency, top/slowest endpoints, per-org traffic + org spotlight,
 * live system stats. Arbitrary date ranges (minute tier 35d, daily tier 5y).
 * All data self-instrumented — nothing synthetic.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Rocket, CalendarDays, Zap, AlertOctagon, Building2, CreditCard, Database, Timer, MemoryStick, Cpu, X, ShieldAlert, Send, CheckCircle2 } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api/v2/super-admin/metrics`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });

const PRESETS = [
  { label: '24h', days: 1 },
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '180d', days: 180 },
  { label: '1y', days: 365 },
];

const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };

const fmtNum = (n) => (n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`);
const fmtTime = (isoStr, interval) => {
  const d = new Date(isoStr);
  if (interval === 'day') return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return d.toLocaleTimeString('en-US', { hour: 'numeric' });
};

const StatCard = ({ icon: Icon, label, value, sub, tone = 'text-gray-900' }) => (
  <div className="bg-white rounded-2xl shadow-sm p-4" data-testid={`mc-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
    <div className="flex items-center gap-2 text-xs font-medium text-gray-500 mb-1.5">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <p className={`text-xl font-bold ${tone}`}>{value}</p>
    {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

const EndpointTable = ({ title, rows, maxCount, testId }) => (
  <div className="bg-white rounded-2xl shadow-sm p-5" data-testid={testId}>
    <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>
    <div className="space-y-1.5 max-h-96 overflow-y-auto">
      {rows.length === 0 && <p className="text-xs text-gray-400">No data in this range.</p>}
      {rows.map((r, i) => (
        <div key={`${r.method}-${r.path}-${i}`} className="relative rounded-lg px-2.5 py-1.5 overflow-hidden">
          <div className="absolute inset-y-0 left-0 bg-blue-50 rounded-lg" style={{ width: `${Math.max(3, (r.count / maxCount) * 100)}%` }} />
          <div className="relative flex items-center gap-2 text-xs">
            <span className="font-mono font-semibold text-gray-500 w-11 shrink-0">{r.method}</span>
            <span className="font-mono text-gray-800 truncate flex-1" title={r.path}>{r.path}</span>
            <span className="text-gray-500 shrink-0">{fmtNum(r.count)}</span>
            <span className={`shrink-0 w-16 text-right ${r.avgMs > 1000 ? 'text-amber-600 font-semibold' : 'text-gray-400'}`}>{r.avgMs}ms</span>
            {r.errors > 0 && <span className="text-red-500 shrink-0">{r.errors} err</span>}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default function SuperAdminMetricsTab() {
  const [preset, setPreset] = useState(1);
  const [startDate, setStartDate] = useState(iso(daysAgo(1)));
  const [endDate, setEndDate] = useState(iso(new Date()));
  const [spotlightOrg, setSpotlightOrg] = useState(null);
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState(null);
  const [endpoints, setEndpoints] = useState(null);
  const [orgs, setOrgs] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (s, e, org) => {
    try {
      const range = `start=${s}&end=${e}`;
      const orgQ = org ? `&orgId=${org.orgId}` : '';
      const [ov, ts, ep, og, al] = await Promise.all([
        axios.get(`${API}/overview`, auth()),
        axios.get(`${API}/timeseries?${range}${orgQ}`, auth()),
        axios.get(`${API}/endpoints?${range}${orgQ}`, auth()),
        axios.get(`${API}/orgs?${range}`, auth()),
        axios.get(`${API}/alerts`, auth()),
      ]);
      setOverview(ov.data); setSeries(ts.data); setEndpoints(ep.data); setOrgs(og.data); setAlerts(al.data);
    } catch {
      toast.error('Could not load metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load(startDate, endDate, spotlightOrg);
    const t = setInterval(() => load(startDate, endDate, spotlightOrg), 60000);
    return () => clearInterval(t);
  }, [startDate, endDate, spotlightOrg, load]);

  const applyPreset = (days) => {
    setPreset(days);
    setStartDate(iso(daysAgo(days)));
    setEndDate(iso(new Date()));
  };

  const applyCustom = (s, e) => {
    setPreset(null);
    if (s) setStartDate(s);
    if (e) setEndDate(e);
  };

  const sendTestAlert = async () => {
    setSendingTest(true);
    try {
      const { data } = await axios.post(`${API}/alerts/test`, {}, auth());
      const ok = Object.entries(data.results || {}).filter(([, v]) => v.success).map(([k]) => k);
      toast.success(ok.length ? `Test alert sent via ${ok.join(' + ')}` : 'Test alert failed on all channels');
      load(startDate, endDate, spotlightOrg);
    } catch {
      toast.error('Could not send test alert');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading && !overview) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }
  if (!overview) return null;

  const r = overview.requests;
  const sys = overview.system;
  const biz = overview.business;
  const points = (series?.points || []).map((p) => ({ ...p, label: fmtTime(p.t, series.interval) }));
  const rangeTotal = points.reduce((a, p) => a + p.requests, 0);
  const rangeErrors = points.reduce((a, p) => a + p.errors, 0);
  const maxTop = Math.max(1, ...(endpoints?.top || []).map((e) => e.count));
  const maxSlow = Math.max(1, ...(endpoints?.slowest || []).map((e) => e.count));
  const maxFail = Math.max(1, ...(endpoints?.failing || []).map((e) => (e.errors || 0) + (e.clientErrors || 0)));

  return (
    <div className="space-y-6" data-testid="superadmin-metrics-tab">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mission Control</h1>
          <p className="text-sm text-gray-500 mt-1">
            Live, self-collected API + system metrics — minute detail for 35 days, daily history for 5 years.
            {r.trackingSince && ` Tracking since ${new Date(r.trackingSince).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-full bg-gray-100 p-0.5">
            {PRESETS.map((p) => (
              <button
                key={p.days}
                onClick={() => applyPreset(p.days)}
                data-testid={`mc-range-${p.label}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${preset === p.days ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <input type="date" value={startDate} max={endDate}
              onChange={(e) => applyCustom(e.target.value, null)}
              data-testid="mc-date-start"
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-700 bg-white" />
            <span className="text-gray-400">→</span>
            <input type="date" value={endDate} min={startDate} max={iso(new Date())}
              onChange={(e) => applyCustom(null, e.target.value)}
              data-testid="mc-date-end"
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-700 bg-white" />
          </div>
          <button onClick={() => load(startDate, endDate, spotlightOrg)} data-testid="mc-refresh" className="p-2 rounded-full hover:bg-gray-100 text-gray-500" aria-label="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stat cards — fixed KPI windows */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard icon={Rocket} label="API Requests (30d)" value={fmtNum(r.total30d)} sub={`all-time: ${fmtNum(r.totalAllTime)}`} />
        <StatCard icon={CalendarDays} label="Requests Today" value={fmtNum(r.today)} />
        <StatCard icon={Zap} label="Avg Response (24h)" value={`${r.avgMs24h}ms`} tone={r.avgMs24h > 800 ? 'text-amber-600' : 'text-gray-900'} />
        <StatCard icon={AlertOctagon} label="Error Rate (24h)" value={`${r.errorRate24h}%`} sub={`${fmtNum(r.errors24h)} of ${fmtNum(r.count24h)}`} tone={r.errorRate24h > 2 ? 'text-red-600' : 'text-emerald-600'} />
        <StatCard icon={Building2} label="Active Orgs (24h)" value={biz.activeOrgs24h} />
        <StatCard icon={CreditCard} label="Paid Checkouts (30d)" value={fmtNum(biz.paidCheckouts30d)} />
        <StatCard icon={Database} label="DB Connections" value={sys.dbConnections} />
        <StatCard icon={Timer} label="DB Latency" value={`${sys.dbLatencyMs}ms`} tone={sys.dbLatencyMs > 50 ? 'text-amber-600' : 'text-gray-900'} />
        <StatCard icon={MemoryStick} label="Memory" value={`${sys.memoryPercent}%`} sub={`app: ${sys.processMemoryMb} MB`} />
        <StatCard icon={Cpu} label="CPU" value={`${sys.cpuPercent}%`} />
      </div>

      {/* Spotlight banner */}
      {spotlightOrg && (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-2xl px-4 py-3" data-testid="mc-spotlight-banner">
          <Building2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <p className="text-sm text-emerald-800 flex-1">
            Spotlight: <strong>{spotlightOrg.name}</strong> — charts and endpoint tables below show only this organization&apos;s traffic.
          </p>
          <button onClick={() => setSpotlightOrg(null)} data-testid="mc-spotlight-clear"
            className="flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900">
            <X className="w-3.5 h-3.5" /> Clear
          </button>
        </div>
      )}

      {/* Range summary */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-600" data-testid="mc-range-summary">
        <span><strong className="text-gray-900">{fmtNum(rangeTotal)}</strong> requests in range</span>
        <span><strong className={rangeErrors > 0 ? 'text-red-600' : 'text-gray-900'}>{fmtNum(rangeErrors)}</strong> server errors</span>
        <span className="text-gray-400 text-xs">{startDate} → {endDate}{spotlightOrg ? ` · ${spotlightOrg.name}` : ' · all traffic'}</span>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="mc-chart-requests">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Requests over time</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={points}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} minTickGap={30} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} width={40} tickFormatter={fmtNum} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10 }} />
              <Bar dataKey="requests" fill="#2563eb" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="mc-chart-latency">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Avg response time (ms)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={points}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} minTickGap={30} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} width={40} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10 }} />
              <Line type="monotone" dataKey="avgMs" stroke="#7c3aed" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-5 lg:col-span-2" data-testid="mc-chart-errors">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Errors over time (5xx)</h3>
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={points}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} minTickGap={30} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} width={40} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10 }} />
              <Area type="monotone" dataKey="errors" stroke="#dc2626" fill="#fee2e2" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Endpoint tables — honor spotlight */}
      <div className="grid lg:grid-cols-2 gap-4">
        <EndpointTable title={spotlightOrg ? `Top endpoints — ${spotlightOrg.name}` : 'Top 20 endpoints'} rows={endpoints?.top || []} maxCount={maxTop} testId="mc-top-endpoints" />
        <EndpointTable title={spotlightOrg ? `Slowest endpoints — ${spotlightOrg.name}` : 'Slowest endpoints (avg)'} rows={endpoints?.slowest || []} maxCount={maxSlow} testId="mc-slowest-endpoints" />
      </div>

      {/* Failing endpoints — which routes error the most in this range */}
      <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="mc-failing-endpoints">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <AlertOctagon className="w-4 h-4 text-red-500" />
            {spotlightOrg ? `Failing endpoints — ${spotlightOrg.name}` : 'Failing endpoints'}
          </h3>
          <span className="text-[11px] text-gray-400">5xx server errors + 4xx client errors in range, worst first</span>
        </div>
        <div className="space-y-1.5 max-h-96 overflow-y-auto">
          {(endpoints?.failing || []).length === 0 && (
            <p className="text-xs text-gray-400" data-testid="mc-failing-empty">No failed requests in this range — everything healthy.</p>
          )}
          {(endpoints?.failing || []).map((r, i) => {
            const fails = (r.errors || 0) + (r.clientErrors || 0);
            const rate = r.count ? Math.round((fails / r.count) * 1000) / 10 : 0;
            return (
              <div key={`${r.method}-${r.path}-${i}`} className="relative rounded-lg px-2.5 py-1.5 overflow-hidden" data-testid={`mc-failing-row-${i}`}>
                <div className="absolute inset-y-0 left-0 bg-red-50 rounded-lg" style={{ width: `${Math.max(3, (fails / maxFail) * 100)}%` }} />
                <div className="relative flex items-center gap-2 text-xs">
                  <span className="font-mono font-semibold text-gray-500 w-11 shrink-0">{r.method}</span>
                  <span className="font-mono text-gray-800 truncate flex-1" title={r.path}>{r.path}</span>
                  {r.errors > 0 && <span className="text-red-600 font-semibold shrink-0">{fmtNum(r.errors)} × 5xx</span>}
                  {r.clientErrors > 0 && <span className="text-amber-600 shrink-0">{fmtNum(r.clientErrors)} × 4xx</span>}
                  <span className="text-gray-400 shrink-0">of {fmtNum(r.count)}</span>
                  <span className={`shrink-0 w-14 text-right font-semibold ${rate >= 50 ? 'text-red-600' : rate >= 10 ? 'text-amber-600' : 'text-gray-500'}`}>{rate}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Requests by organization — cost allocation + click to spotlight */}
      <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="mc-orgs">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900">Requests by organization</h3>
          <span className="text-[11px] text-gray-400">% of total traffic in range · click an org to spotlight it</span>
        </div>
        <div className="space-y-1.5">
          {(orgs?.orgs || []).length === 0 && <p className="text-xs text-gray-400">No data in this range.</p>}
          {(orgs?.orgs || []).map((o, i) => (
            <button
              key={o.orgId || `pub-${i}`}
              onClick={() => o.orgId && setSpotlightOrg(spotlightOrg?.orgId === o.orgId ? null : o)}
              data-testid={`mc-org-row-${i}`}
              disabled={!o.orgId}
              className={`relative w-full text-left rounded-lg px-2.5 py-1.5 overflow-hidden transition-colors ${o.orgId ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'} ${spotlightOrg?.orgId === o.orgId ? 'ring-1 ring-emerald-400' : ''}`}
            >
              <div className="absolute inset-y-0 left-0 bg-emerald-50 rounded-lg" style={{ width: `${Math.max(2, o.percent || 0)}%` }} />
              <div className="relative flex items-center gap-2 text-xs">
                <span className="font-medium text-gray-800 truncate flex-1">{o.name}</span>
                <span className="text-gray-400 shrink-0">{o.avgMs}ms avg</span>
                <span className="text-gray-500 shrink-0">{fmtNum(o.requests)} req</span>
                {o.errors > 0 && <span className="text-red-500 shrink-0">{o.errors} err</span>}
                <span className="font-semibold text-emerald-700 shrink-0 w-12 text-right" data-testid={`mc-org-percent-${i}`}>{o.percent}%</span>
              </div>
            </button>
          ))}
        </div>
        {orgs?.totalRequests > 0 && (
          <p className="text-[11px] text-gray-400 mt-3">
            Cost allocation base: {fmtNum(orgs.totalRequests)} total requests in range — each org&apos;s share of platform traffic.
          </p>
        )}
      </div>

      {/* Critical endpoint watchdog */}
      {alerts && (
        <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="mc-watchdog">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-rose-500" /> Critical endpoint watchdog
            </h3>
            <button
              onClick={sendTestAlert}
              disabled={sendingTest}
              data-testid="mc-watchdog-test"
              className="flex items-center gap-1.5 text-xs font-semibold text-white bg-gray-900 hover:bg-gray-700 rounded-full px-3.5 py-1.5 disabled:opacity-50 transition-colors"
            >
              {sendingTest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Send test alert
            </button>
          </div>
          <p className="text-[11px] text-gray-400 mb-3">
            Any 5xx on a money route alerts instantly via email + SMS + WhatsApp ({alerts.recipients?.email}{alerts.recipients?.phone ? ` · ${alerts.recipients.phone}` : ''}),
            re-alerts every {alerts.policy?.cooldownMin} min while failing, all-clear after {alerts.policy?.recoveryAfterMin} clean minutes.
          </p>
          <div className="grid sm:grid-cols-2 gap-1.5 mb-4">
            {(alerts.endpoints || []).map((ep) => (
              <div key={ep.id} data-testid={`mc-watchdog-ep-${ep.id}`} className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 bg-gray-50 text-xs">
                <span className={`w-2 h-2 rounded-full shrink-0 ${ep.alerting ? 'bg-red-500 animate-pulse' : 'bg-emerald-500'}`} />
                <span className="font-medium text-gray-800 truncate flex-1">{ep.label}</span>
                {ep.failures15m > 0
                  ? <span className="text-red-600 font-semibold shrink-0">{ep.failures15m} fail / 15m</span>
                  : <span className="text-gray-400 shrink-0">healthy</span>}
              </div>
            ))}
          </div>
          <h4 className="text-xs font-semibold text-gray-700 mb-1.5">Recent alerts</h4>
          <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="mc-watchdog-log">
            {(alerts.log || []).length === 0 && <p className="text-xs text-gray-400">No alerts fired yet — all money routes healthy.</p>}
            {(alerts.log || []).map((l, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                {l.kind === 'recovery'
                  ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  : <AlertOctagon className={`w-3.5 h-3.5 shrink-0 ${l.kind === 'test' ? 'text-gray-400' : 'text-red-500'}`} />}
                <span className="font-medium text-gray-800">{l.endpoint}</span>
                <span className="text-gray-400">{l.kind === 'test' ? 'test alert' : l.kind === 'recovery' ? 'all clear' : `${l.failures15m} failures`}</span>
                <span className="text-gray-400 ml-auto shrink-0">{new Date(l.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
